import { AssetContainer, Camera, Engine, Mesh, Scene, TransformNode, Vector3 } from 'babylonjs';
import ContentBase from '@webcore/content/core/content.base';
import { disposeModelCache, getModelContainer } from '@webcore/core/loader/loader.modelinstance';
import { extractMeshSnapshot } from './mesh.extract';
import { mergeSnapshots } from '../solver/mesh.merge';
import type { AnalysisSnapshot } from '../solver/types';

export interface PlaneFinderBounds {
  center: Vector3;
  radius: number;
  diag: number;
}

/**
 * Loads a dropped GLB model as real meshes (own materials — needed for the FX
 * plugin + unlit capture swap), exposes them, and extracts a merged world-space
 * analysis snapshot. The snapshot's mesh order matches `planeFinderMeshes` so faceIds and
 * per-mesh wave depths line up.
 */
export class PlaneFinderContent extends ContentBase {
  private url: string;
  private meshes: Mesh[] = [];
  private container?: AssetContainer;
  // Own copy of the per-mesh triangle offsets: the snapshot's buffers get
  // transferred to the analysis worker (detached on the main thread), so faceId
  // mapping must not read from snapshot.meshTriOffset afterwards.
  private meshTriOffset?: Uint32Array;
  snapshot?: AnalysisSnapshot;
  bounds?: PlaneFinderBounds;

  constructor(url: string) {
    super();
    this.url = url;
  }

  override async init(engine: Engine, scene: Scene, camera: Camera, rootNode?: TransformNode): Promise<void> {
    super.init(engine, scene, camera, rootNode);
    const container = await getModelContainer(this.url, scene, '.glb');
    container.addAllToScene();
    this.container = container;

    // Plane Finder operates on a STATIC surface — freeze any animations to the
    // first frame so the mesh doesn't move while detecting/framing/capturing (a
    // moving mesh breaks picking and bakes a moving pose into the snapshot).
    for (const group of container.animationGroups) {
      group.stop();
      group.reset(); // not-started → play → goToFrame(0) → stop, i.e. frozen at frame 0
    }

    this.meshes = container.meshes.filter(
      (m): m is Mesh => m instanceof Mesh && m.getTotalVertices() > 0,
    );
    // Tripwire: if geometry loaded but NOTHING passed the `instanceof Mesh` filter,
    // the bundle almost certainly contains two Babylon copies (class identity breaks
    // across them) — see `resolve.dedupe` in vite.config.ts. This exact failure
    // shipped once and was silent: the model rendered but was untappable.
    if (this.meshes.length === 0 && container.meshes.some((m) => m.getTotalVertices() > 0)) {
      console.error(
        '[plane-finder] model loaded but no mesh passed the instanceof filter — ' +
          'likely TWO bundled Babylon copies (check resolve.dedupe in vite.config.ts)',
      );
    }
    for (const mesh of this.meshes) {
      mesh.isPickable = true;
      mesh.metadata = { ...(mesh.metadata ?? {}), ejPlaneFinder: true };
    }

    const snaps = this.meshes.map((m) => extractMeshSnapshot(m));
    this.snapshot = mergeSnapshots(snaps);
    this.meshTriOffset = Uint32Array.from(this.snapshot.meshTriOffset);
    this.bounds = computeBounds(this.snapshot);
  }

  get planeFinderMeshes(): Mesh[] {
    return this.meshes;
  }

  get triangleCount(): number {
    return this.snapshot?.triangleCount ?? 0;
  }

  /** Map a picked (mesh, faceId) to a merged-snapshot triangle index, or -1. */
  faceToMergedTriangle(mesh: Mesh, faceId: number): number {
    const idx = this.meshes.indexOf(mesh);
    if (idx < 0 || !this.meshTriOffset) return -1;
    return this.meshTriOffset[idx] + faceId;
  }

  override dispose(): void {
    for (const mesh of this.meshes) mesh.dispose();
    if (this.scene) disposeModelCache(this.scene);
    this.meshes = [];
    this.container = undefined;
    this.snapshot = undefined;
    this.meshTriOffset = undefined;
    this.bounds = undefined;
  }
}

function computeBounds(snapshot: AnalysisSnapshot): PlaneFinderBounds {
  let minX = Infinity, minY = Infinity, minZ = Infinity;
  let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
  const p = snapshot.positions;
  for (let v = 0; v < p.length / 3; v++) {
    const x = p[v * 3], y = p[v * 3 + 1], z = p[v * 3 + 2];
    if (x < minX) minX = x; if (y < minY) minY = y; if (z < minZ) minZ = z;
    if (x > maxX) maxX = x; if (y > maxY) maxY = y; if (z > maxZ) maxZ = z;
  }
  if (!Number.isFinite(minX)) {
    return { center: Vector3.Zero(), radius: 1, diag: 1 };
  }
  const center = new Vector3((minX + maxX) / 2, (minY + maxY) / 2, (minZ + maxZ) / 2);
  const diag = Math.sqrt((maxX - minX) ** 2 + (maxY - minY) ** 2 + (maxZ - minZ) ** 2);
  return { center, radius: Math.max(diag / 2, 1e-3), diag };
}
